// frontend/src/utils/transferDisplayUtils.js
/**
 * Human-readable From/To party labels for AssetTransfer rows.
 *
 * Workshop moves (repair / repair_return) store the garage on to_workshop;
 * showing "Inventory" for the workshop side is misleading.
 *
 * Employee parties follow design-system-reference.md inline metadata:
 *   "John Smith · S0102" (middle-dot separator, never parentheses).
 */

const WORKSHOP_LABEL = (transfer) =>
  transfer?.to_workshop_name
  || transfer?.location_from
  || transfer?.location_to
  || 'Workshop';

/** @param {string|null|undefined} name */
/** @param {string|null|undefined} staffId */
export function formatEmployeeParty(name, staffId) {
  if (!name && !staffId) return null;
  if (name && staffId) return `${name} · ${staffId}`;
  return name || staffId;
}

/** @param {object} transfer AssetTransfer serializer payload */
export function getTransferFromParty(transfer) {
  if (!transfer) return 'Inventory';
  if (transfer.transfer_type === 'repair_return') {
    return WORKSHOP_LABEL(transfer);
  }
  return formatEmployeeParty(transfer.from_employee_name, transfer.from_employee_staff_id) || 'Inventory';
}

/**
 * Lease hand-back has no StaffUser on the receiving side. The lessor name
 * lives on location_to. Match the raw key and the human label so a payload
 * that only echoed `lease_return` as its display string still names the
 * lessor instead of falling through to "Inventory".
 *
 * @param {object} transfer
 * @returns {boolean}
 */
function isLeaseReturn(transfer) {
  const type = transfer?.transfer_type;
  if (type === 'lease_return') return true;
  const display = String(transfer?.transfer_type_display || '').trim().toLowerCase();
  return display === 'lease_return' || display === 'returned to lessor';
}

/** @param {object} transfer AssetTransfer serializer payload */
export function getTransferToParty(transfer) {
  if (!transfer) return 'Inventory';
  if (transfer.transfer_type === 'repair') {
    return WORKSHOP_LABEL(transfer);
  }
  // Access-control revocation has no destination party — access is switched
  // off rather than handed back to inventory.
  if (transfer.transfer_type === 'access_deactivation') {
    return 'Deactivated';
  }
  // Hand-back to the lessor. "Inventory" would mean the unit is still ours.
  if (isLeaseReturn(transfer)) {
    return (transfer.location_to || '').trim() || 'Lessor';
  }
  // Person wins over project. Assigned desktops/laptops on a site still have a
  // human holder; project-only rows (monitor deployments) have no to_employee.
  const employee = formatEmployeeParty(
    transfer.to_employee_name,
    transfer.to_employee_staff_id,
  );
  if (employee) return employee;
  if (transfer.to_project_name) {
    return transfer.to_project_code
      ? `${transfer.to_project_name} · ${transfer.to_project_code}`
      : transfer.to_project_name;
  }
  return 'Inventory';
}

/** @param {object} transfer AssetTransfer serializer payload */
export function formatTransferRoute(transfer) {
  return `${getTransferFromParty(transfer)} → ${getTransferToParty(transfer)}`;
}

/**
 * AppSheet-style site hop. Empty when both ends are blank.
 * Same-site transfers collapse to one label (no fake "A → A").
 *
 * @param {object} transfer AssetTransfer serializer payload
 * @returns {string|null}
 */
export function formatTransferLocationRoute(transfer) {
  const from = (transfer?.location_from || '').trim();
  const to = (transfer?.location_to || '').trim();
  if (!from && !to) return null;
  if (from && to && from === to) return from;
  if (from && to) return `${from} → ${to}`;
  return to || from;
}
